import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Map, Moon } from 'lucide-react';
import { useProgression } from '@/hooks/useProgression';
import { useLanguage } from '@/i18n/LanguageContext';
import { safeGetJSON, safeSetItem } from '@/lib/safeStorage';

interface SessionData {
  startTime: number;
  roomsVisited: string[];
}

interface ExitRitualOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmExit: () => void;
}

const SESSION_KEY = 'human-rooms-current-session';

const exitMessages = [
  "Before you go, take one breath.",
  "The rooms will remember you.",
  "What stays with you?",
  "You leave, but something remains.",
  "Until next time, traveler.",
  "The door closes softly behind you.",
  "What did you discover?",
  "Carry this with you, or leave it behind.",
];

const ExitRitualOverlay = ({ isOpen, onClose, onConfirmExit }: ExitRitualOverlayProps) => {
  const { state } = useProgression();
  const { t } = useLanguage();
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [exitMessage] = useState(() => 
    exitMessages[Math.floor(Math.random() * exitMessages.length)]
  );

  useEffect(() => {
    if (isOpen) {
      const data = safeGetJSON<SessionData>(SESSION_KEY, null);
      setSessionData(data);
    }
  }, [isOpen]);

  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return 'less than a minute';
    if (minutes === 1) return '1 minute';
    return `${minutes} minutes`;
  };

  const sessionDuration = sessionData 
    ? Date.now() - sessionData.startTime 
    : 0;

  const roomsThisSession = sessionData?.roomsVisited?.length || 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative max-w-md w-full mx-4 text-center"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute -top-12 right-0 p-2 text-white/30 hover:text-white/60 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Main content */}
            <div className="space-y-8">
              {/* Exit message */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 1 }}
                className="text-2xl font-light text-white/80 leading-relaxed"
              >
                {exitMessage}
              </motion.p>

              {/* Session summary */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="space-y-4 py-6 border-y border-white/10"
              >
                <p className="text-xs uppercase tracking-widest text-white/30">
                  {t('session_summary') || 'This Session'}
                </p>
                
                <div className="flex justify-center gap-8">
                  <div className="text-center">
                    <Clock className="w-4 h-4 text-white/30 mx-auto mb-2" />
                    <p className="text-lg text-white/70">{formatDuration(sessionDuration)}</p>
                    <p className="text-xs text-white/30">{t('time_spent') || 'spent here'}</p>
                  </div>
                  
                  <div className="text-center">
                    <Map className="w-4 h-4 text-white/30 mx-auto mb-2" />
                    <p className="text-lg text-white/70">{roomsThisSession}</p>
                    <p className="text-xs text-white/30">{t('rooms_visited_session') || 'rooms visited'}</p>
                  </div>
                </div>

                {/* Total journey stats */}
                <p className="text-xs text-white/20 pt-4">
                  {t('total_journey') || 'Total journey'}: {state.completedRooms.length} {t('rooms_explored') || 'rooms explored'}
                </p>
              </motion.div>

              {/* Action buttons */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.5 }}
                className="flex flex-col gap-3"
              >
                <button
                  onClick={onConfirmExit}
                  className="w-full py-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white/70 hover:text-white transition-all duration-300 rounded-lg text-sm"
                >
                  {t('close_the_door') || 'Close the Door'}
                </button>
                
                <button
                  onClick={onClose}
                  className="w-full py-3 text-white/30 hover:text-white/50 transition-colors text-xs"
                >
                  {t('stay_longer') || 'Stay a little longer'}
                </button>
              </motion.div>

              {/* Subtle goodbye */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="text-xs text-white/15 flex items-center justify-center gap-2"
              >
                <Moon className="w-3 h-3" />
                {t('the_rooms_wait') || 'The rooms will wait for you.'}
              </motion.p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitRitualOverlay;

// Helper function to track session
export const startSession = () => {
  const existing = safeGetJSON<SessionData>(SESSION_KEY, null);
  if (!existing || Date.now() - existing.startTime > 30 * 60 * 1000) {
    // Start new session if none exists or it's been more than 30 minutes
    safeSetItem(SESSION_KEY, JSON.stringify({
      startTime: Date.now(),
      roomsVisited: [],
    }));
  }
};

export const trackRoomVisit = (roomId: string) => {
  const existing = safeGetJSON<SessionData>(SESSION_KEY, { startTime: Date.now(), roomsVisited: [] });
  if (!existing.roomsVisited.includes(roomId)) {
    existing.roomsVisited.push(roomId);
    safeSetItem(SESSION_KEY, JSON.stringify(existing));
  }
};

export const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
};
